import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FileDown, LogOut, Plus, Printer, Trash2, Upload, X } from "lucide-react";
import { LogoMark } from "@/components/Navigation";

const API = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];

async function api(path, opts = {}) {
  const res = await fetch(`${API}/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (!res.ok) {
    let msg = "Fehler — bitte erneut versuchen";
    try {
      const j = await res.json();
      const d = j.detail;
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((e) => (e && e.msg) || "").filter(Boolean).join(", ") || msg;
      else if (d && typeof d.msg === "string") msg = d.msg;
      else if (typeof j === "string") msg = j;
    } catch (_) { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

const eur = (v) =>
  Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

const eurEn = (v) =>
  "€" + Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ISSUER = {
  name: "Joudi Group Ltd",
  address: "Tassou Markou\n8560 Peyia, Paphos\nCyprus",
  web: "joudi-group.com",
  phone: "+357 97591684",
  taxId: "HE499162",
};

const REVERSE_CHARGE =
  "Diese Leistung ist nicht in Zypern steuerbar — Umkehrung der Steuerpflicht (Reverse Charge) gemäß Art. 196 MwSt-SystRL 2006/112/EG. Die Steuerschuld geht auf den Leistungsempfänger über. | This supply is subject to the reverse charge under Article 196 of Directive 2006/112/EC — VAT is to be accounted for by the recipient.";

function today() {
  return new Date().toISOString().slice(0, 10);
}

const STATUS_LABEL = { draft: "ENTWURF", sent: "GESENDET", paid: "BEZAHLT" };
const STATUS_COLOR = { draft: "text-bone/60 bg-bone/10", sent: "text-gold bg-goldsoft", paid: "text-emerald-300 bg-emerald-900/40" };
const TAB_ACTIVE = "rounded-[3px] bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink";
const TAB_IDLE = "rounded-[3px] border border-bone/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-bone/70 transition-colors hover:border-gold hover:text-gold";

/* ---------------- Angebot: Konstanten & Vorlagen ---------------- */

const OFFER_ISSUER = {
  brand: "DICKIES-CAMPUS",
  brandSub: "Akademie für Pflege",
  name: "Joudi Group Ltd",
  address: "Tassou Markou\n8560 Peyia, Paphos\nZypern (Cyprus)",
  footer: "Joudi Group Ltd · Tassou Markou, 8560 Peyia, Paphos, Zypern · joudi-group.com",
};

const OFFER_STATUS_LABEL = { draft: "ENTWURF", sent: "GESENDET", accepted: "ANGENOMMEN", declined: "ABGELEHNT" };
const OFFER_STATUS_COLOR = { draft: "text-bone/60 bg-bone/10", sent: "text-gold bg-goldsoft", accepted: "text-emerald-300 bg-emerald-900/40", declined: "text-red-300 bg-red-500/10" };

const dEUR = (d) => (/^\d{4}-\d{2}-\d{2}$/.test(d || "") ? d.split("-").reverse().join(".") : d || "");

const addDays = (dateStr, days) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr || "")) return "";
  const dt = new Date(dateStr + "T00:00:00Z");
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
};

const OFFER_TPL_LMS = {
  title: "Digitale Lernplattform „[Kundenname]-Campus“",
  subtitle: "Maßgeschneidertes LMS für zertifizierte Pflege-Fort- und Weiterbildungen (AZAV-konform)",
  intro: "",
  items: [
    { title: "Konzeption, UX & Corporate Design", scope: "", description: "Markenauftritt mit hohem Wiedererkennungswert, eigene Typografie, durchgängige Designsprache für Web und alle PDF-Dokumente", amount: 8000 },
    { title: "LMS-Kernplattform", scope: "", description: "Teilnahmeverwaltung mit Login, Kurs- & Modulsystem, Lese-Fortschritt, Teilnahme-Quiz je Modul, Lernkarten-Erarbeitung nach AZAV-Soll, UI ab ca. 45 Min., Einsatzzeiten", amount: 22500 },
    { title: "AZAV-Validierung Modul", scope: "", description: "Zwei stufenweise genehmigte Kursvarianten (BG-Maßnahme, Selbstzahler), zusätzliche Teilnehmerklassen, Tageslernziel-System, AZAV-konforme Schulungsplan-PDFs, QM-Formularwesen (Pflichtdokumente, Listen, Bewertungsbögen etc.)", amount: 11500 },
    { title: "PDF-Engine", scope: "", description: "Kurspläne (bis ca. 300 Seiten als gestaltetes Buch-PDF), Zertifikate mit Echtheits-Verifikation, Zahlungscenter, Archivierung & zentrale Ablage, fehlerfreier Abgleich", amount: 9000 },
    { title: "KI-Content-Workflow mit Freigabe-Workflow", scope: "", description: "KI-gestützte Aufbereitung der Lerninhalte, verpflichtende fachliche Prüfung & Freigabe vor Veröffentlichung, Diff-Ansicht, Massenverarbeitung", amount: 7000 },
    { title: "Content-Produktion", scope: "", description: "36 Kurse, 648 Lernmodule, 5.880 Quizfragen, 144 AZAV-Tages-Arbeitsblätter, intensive Überarbeitung — inkl. Qualitätsprüfung und AZAV-konformer Wortwahl", amount: 13500 },
    { title: "Online-Prüfungssystem", scope: "", description: "Native Prüfungen (schriftlich & mündlich dokumentiert), Beauftragung, Auswertung", amount: 4500 },
    { title: "Admin-Console", scope: "", description: "Fortschritts-Dashboard, Lernzeit-Kontrolle manuell mit Plausibilitäts-Deckel, Anwesenheits-/Arbeitsstunden-Konzept, digitale Unterschrift am Modulende, E-Mail-Versand", amount: 8300 },
    { title: "Qualitätssicherung, Testing & Produktivstellung", scope: "", description: "Automatisierte Tests, Daten-Synchronisation bei Deployment, Duplikat-Schutz, Go-Live-Begleitung", amount: 5700 },
  ],
  monthly: [
    { title: "Hosting, Betrieb, Backups, SSL, Monitoring", scope: "", description: "", amount: 290 },
    { title: "KI-Kontingent (Content-Erzeugung & Übungen, fair use)", scope: "", description: "", amount: 150 },
    { title: "Wartung & Support", scope: "", description: "bis 8 Std./Monat, Reaktionszeit 1 Werktag", amount: 880 },
  ],
  terms: [
    { heading: "3. Projektablauf & Zeitplan", text: "Phase 1 — Konzeption & Design (4 Wochen) · Phase 2 — Kernplattform & PDF-Engine (6–10 Wochen) · Phase 3 — AZAV-Modul, Prüfungen, Admin-Cockpits (6–8 Wochen) · Phase 4 — Content-Produktion & fachliche Freigaben (parallel, 8 Wochen) · Phase 5 — QS, Abnahme, Go-Live (2 Wochen)\n\nGesamtlaufzeit: ca. 6–7 Monate ab Beauftragung." },
    { heading: "4. Zahlungsplan", text: "30 % bei Beauftragung · 30 % nach Abnahme Phase 2 · 30 % nach Abnahme Phase 3 · 10 % bei Go-Live." },
    { heading: "5. Hinweise", text: "Alle Inhalte werden vor Veröffentlichung durch die Fachschaft des Auftraggebers freigegeben (rechtliche Verantwortung verbleibt beim Auftraggeber).\nAZAV-Zertifikatsunterlagen werden exakt nach den genehmigten Schulungsplänen umgesetzt.\nQuellcode und Inhalte gehen nach vollständiger Zahlung in das Eigentum des Auftraggebers über.\nAlle Preise verstehen sich netto — Umkehrung der Steuerpflicht (Reverse Charge) gemäß Art. 196 MwSt-SystRL i. V. m. § 13b UStG; die Umsatzsteuer ist vom Leistungsempfänger zu schulden." },
  ],
};

const OFFER_TPL_CRM = {
  title: "Konzeption, Entwicklung und Einführung eines individuellen CRM- und Verwaltungssystems für Bildungsträger",
  subtitle: "",
  items_heading: "Vergütung (Festpreis) — Übersicht je Phase",
  intro: "Sehr geehrte Damen und Herren,\nvielen Dank für das Gespräch und Ihr Interesse. Gerne biete ich Ihnen die Konzeption, Entwicklung und Einführung eines individuellen CRM- und Verwaltungssystems an. Das System ist auf die Abläufe eines AZAV-zertifizierten Bildungsträgers zugeschnitten. Es deckt den gesamten Prozess ab: Kundengewinnung, Teilnehmeraufnahme, Kursdurchführung, Anwesenheit, Prüfung und Zertifikat, Abrechnung, Mahnwesen und die Vorbereitung von AZAV-Audits.",
  items: [
    { title: "Phase 1 — Konzept, Architektur und Grundsystem", scope: "", description: "", amount: 24000 },
    { title: "Phase 2 — Teilnehmer, Kunden und Angebote", scope: "", description: "", amount: 21500 },
    { title: "Phase 3 — Kurse, Unterricht und Anwesenheit", scope: "", description: "", amount: 21500 },
    { title: "Phase 4 — Rechnungswesen, Mahnwesen und Vertrieb", scope: "", description: "", amount: 24000 },
    { title: "Phase 5 — Prüfungen, Bildungsgutschein und AZAV/QM", scope: "", description: "", amount: 26500 },
    { title: "Phase 6 — Organisation, Test, Schulung und Livegang", scope: "", description: "", amount: 17500 },
  ],
  monthly: [],
  terms: [
    { heading: "Ausgangslage", text: "Viele Bildungsträger arbeiten heute mit mehreren getrennten Werkzeugen: Datenbanken, Excel-Listen, Word-Vorlagen, Papier-Anwesenheitslisten und manuell ausgefüllten Formularen. Das hat typische Folgen:\nDaten werden mehrfach gepflegt, Bestände widersprechen sich\nRechnungen, Zertifikate und Nachweise kosten viel Handarbeit\nOffene Posten, Raten und Mahnstufen sind schwer zu überblicken\nDie Vorbereitung eines AZAV-Audits dauert lange, weil Nachweise verteilt liegen\nBildungsgutschein-Formulare werden von Hand ausgefüllt" },
    { heading: "Ziel des Projekts", text: "Ziel ist ein zentrales, browserbasiertes System, das:\nalle Stammdaten (Teilnehmer, Kunden, Kurse, Maßnahmen) an einem Ort führt\nwiederkehrende Dokumente automatisch im Firmendesign erzeugt\nFinanzen (Rechnungen, Raten, Mahnungen, Guthaben) transparent abbildet\nalle AZAV-relevanten Nachweise laufend mitführt und per Knopfdruck ausgibt\ndie bestehenden Daten vollständig übernimmt\nfür alle Mitarbeitenden ohne Installation im Browser nutzbar ist" },
    { heading: "Leistungsumfang im Detail — Phase 1: Konzept, Architektur und Grundsystem", text: "1.1 Anforderungsanalyse und Konzept\nKick-off-Workshop mit der Geschäftsführung und den Fachbereichen\nAufnahme aller Kernprozesse: Anmeldung, Kursdurchführung, Abrechnung, Mahnung, Prüfung, Audit\nSichtung der vorhandenen Vorlagen, Formulare und des QM-Handbuchs\nErstellung eines Datenmodells (Teilnehmer, Kunden, Kurse, Rechnungen usw.)\nFestlegung der Rollen und Berechtigungen\nErgebnis: schriftliches Fachkonzept zur Freigabe durch den Auftraggeber\n\n1.2 Technische Grundarchitektur\nWeb-Oberfläche (Browser, auch auf Tablet und Smartphone nutzbar)\nServer-Anwendung mit Programmierschnittstelle\nDatenbank\nEinrichtung der Live-Umgebung und einer Test-Umgebung\nVerschlüsselte Verbindung (HTTPS)\n\n1.3 Benutzerverwaltung\nLogin mit persönlichem Benutzerkonto\nRollen, z. B. Geschäftsführung, Verwaltung, Vertrieb, Dozent\nRechtevergabe je Rolle\nAktivitätsprotokoll: Wer hat wann was angelegt oder geändert\n\n1.4 Datenübernahme\nAnalyse des Altsystems und der vorhandenen Listen\nÜbernahme von Teilnehmern, Kunden, Kursen, Bildungsgutscheinen und Kostenträgern\nBereinigung von Dubletten, Zuordnung der Teilnehmer zu Kunden\nPrüfprotokoll der Datenübernahme\n\n1.5 Datensicherung\nAutomatische tägliche Backups\nDatenbank-Snapshots vor größeren Änderungen\nOptional Anbindung eines lokalen Speichers (NAS) beim Auftraggeber\nWiederherstellungstest" },
    { heading: "Phase 2: Teilnehmer, Kunden und Angebote", text: "2.1 Teilnehmerverwaltung\nTeilnehmerakte mit Stammdaten, Kontakt und Adresse\nFinanzierungsart: Selbstzahler, Arbeitgeber oder Bildungsgutschein\nSicherheitssperre: keine Rechnung bei Bildungsgutschein-Teilnehmern\nKursart pro Teilnehmer (z. B. Morgenkurs, Abendkurs, E-Learning)\nZuordnung zu Kurs, Maßnahme und Kunde\nAnhänge pro Teilnehmer (Upload, Vorschau, Download, Löschen)\nStatus- und Filterfunktionen, z. B. aktiv, abgeschlossen, „fertig ohne Zertifikat“\nExport der Teilnehmerlisten\n\n2.2 Erstgespräch und Befragung\nDigitales Erstgespräch mit Checkliste nach QM-Vorgabe, als PDF\nTeilnehmerbefragung mit Auswertung, als PDF\n\n2.3 Kundenverwaltung (Einrichtungen und Unternehmen)\nKundenakte mit Ansprechpartnern\nDetailansicht, direkt bearbeitbar\nKontaktprotokoll mit Notizen, Wiedervorlage und Erledigt-Status\nÜbersicht der Teilnehmer je Kunde\nDubletten-Warnung beim Anlegen\nKundenklassifikation und Top-Kunden-Auswertung\nImport und Export\n\n2.4 Angebote\nAngebotserstellung mit Positionen\nAngebots-PDF im Firmendesign\nAngebote für Bildungsgutschein-Teilnehmer mit Kursauswahl\nAngebotsübersicht" },
    { heading: "Phase 3: Kurse, Unterricht und Anwesenheit", text: "3.1 Maßnahmen und Kurse\nMaßnahmenverwaltung mit Preisen, AZAV-Maßnahmenummern und Zulassungszeiträumen\nKursvorlagen mit vollständigem Curriculum (Module, Unterrichtseinheiten)\nStundenpläne je Kurstyp\nMaßnahme starten und abschließen\nPraktikumsnachweise hochladen und herunterladen\nWeiterbildungsverwaltung\n\n3.2 Digitales Klassenbuch\nKlassenbuch pro Kurs mit Datum, Thema, Unterrichtseinheiten und Dozent\nAutomatisches Befüllen aus dem Stundenplan\nEigenes Klassenbuch für Bildungsgutschein-Kurse (je Kurs, Teilnehmer und Tag)\nKlassenbuch-PDF pro Teilnehmer und für den ganzen Kurs\nAutomatische Ablage in der Teilnehmerakte\nArchiv abgeschlossener Klassenbücher\n\n3.3 Anwesenheit\nAnwesenheitserfassung pro Tag und Teilnehmer\nSammelbearbeitung für ganze Kurstage\nAutomatische Berechnung der Anwesenheitsquote\nAnwesenheitslisten als PDF\nDigitale Unterschrift über eine eigene Signaturseite\nSammel-Export aller Anwesenheitslisten eines Kurses (ZIP)\n\n3.4 Dozentenansicht\nEigener Zugang für Dozenten mit Kursen, Klassenbuch und Anwesenheit\nKein Zugriff auf Finanz- und Kundendaten" },
    { heading: "Phase 4: Rechnungswesen, Mahnwesen und Vertrieb", text: "4.1 Rechnungen\nRechnungserstellung mit beliebig vielen Positionen\nSchnellauswahl aus allen Kursen mit hinterlegten Preisen\nEmpfänger: Teilnehmer, Kunde, Agentur für Arbeit oder freie Eingabe\nRabatt in Prozent oder als Festbetrag\nLeistungszeitraum und Einleitungstext je Rechnung\nFortlaufender Rechnungsnummernkreis\nRechnungs-PDF nach DIN 5008 im Firmendesign\nAutomatischer Hinweis auf die Umsatzsteuerbefreiung von Bildungsleistungen (§ 4 Nr. 21 UStG)\nVersand per E-Mail aus dem System, mit Vorlage und Versandprotokoll\nInterne Notizen je Rechnung\n\n4.2 Zahlungen und Raten\nEinmalzahlung oder Ratenzahlung mit 1–12 Raten und frei wählbaren Fälligkeiten\nTeilzahlungen erfassen\nZahlungen zu Rechnungen zuordnen\nZahlungsziel verlängern\nÜbersicht aller offenen Raten\n\n4.3 Storno, Gutschrift und Guthaben\nStornorechnung bzw. Gutschrift als PDF\nStorno rückgängig machen\nGuthaben-Verrechnung: Ein Restguthaben wird auf einer neuen Rechnung abgezogen, ausgewiesen wird der offene Restbetrag\n\n4.4 Mahnwesen\nMahnstufen mit Vorschau, PDF und E-Mail-Versand\nManueller Mahnungs-Dialog für Ratenzahler (frei wählbare Rate, Betrag, Datum, Mahngebühr)\nAutomatisch vorausgefüllte Mahn-E-Mails\nMahnhistorie, „als versendet markieren“, Mahnstatus zurücksetzen\n\n4.5 Auswertungen\nStatistiken zu offenen, bezahlten und überfälligen Rechnungen\nUmsatzübersicht nach Zeitraum\n\n4.6 Vertrieb\nInteressentenverwaltung\nVertriebs-Pipeline als Kanban-Board mit Drag & Drop\nAkquise-Status je Kunde\nVertriebs-Cockpit mit Kennzahlen\nVertriebsmitarbeiter-Verwaltung\nProvisionsabrechnung: Ein Rabatt senkt automatisch den Provisionssatz, und es wird eine Prüfaufgabe angelegt" },
    { heading: "Phase 5: Prüfungen, Bildungsgutschein und AZAV/QM", text: "5.1 Prüfungen\nPrüfungstermine, Ergebnis bestanden/nicht bestanden, Wiederholungsprüfung\nAuswertung der Bestehensquote\n\n5.2 Zertifikate\nZertifikatsvorlagen im Firmendesign\nFortlaufender Zertifikats-Nummernkreis\nZertifikat-PDF mit Stempel und Unterschrift\nStatus und Rückscan-Upload\nAusstellungsprotokoll\nBerücksichtigung länderspezifischer Vorgaben (nach Absprache)\n\n5.3 Bildungsgutschein\nVerwaltung aller Bildungsgutscheine\nEinstellungen je BG-Kurs\nAutomatisches Ausfüllen der BG-Formulare:\nUpload des gescannten Original-Bildungsgutscheins\nVisueller Editor: Datenfelder wie Maßnahmenummer, Zulassungsdauer, Teilnahmezeitraum, Ort und Datum werden per Drag & Drop exakt auf das Formular gelegt\nFeldpositionen werden pro Kurs gespeichert, damit jedes Formular nur einmal eingerichtet werden muss\nFertiges PDF mit Stempel und Unterschrift, automatisch in der Teilnehmerakte abgelegt\n\n5.4 AZAV und Qualitätsmanagement\nAZAV-Checkliste je Teilnehmer und Maßnahme\nVollständigkeitsampel je Teilnehmerakte\nPrüfmappe je Teilnehmer\nExport von Teilnehmerakte und Maßnahmeakte\nAZAV-Ordner mit Ansicht je Kurs\nQM-Dokumente nach dem QM-Handbuch des Auftraggebers, einzeln herunterladbar\nAuditor-Paket: ein Klick erzeugt ein ZIP mit allen Nachweisen, darunter Maßnahmeverträge, Klassenbücher, Anwesenheitslisten, Erstgespräch-Checklisten, Vergleichswerte (Erfolgs- und Verbleibsquoten), Organigramm und weitere Nachweise\nAntrags-Assistent für neue Maßnahmen\nAZAV-Selbstcheck\nKostenkalkulation nach der Empfehlung der Bundesagentur für Arbeit\nVerbleibsbefragung mit Auswertung" },
    { heading: "Phase 6: Organisation, Test, Schulung und Livegang", text: "6.1 Organisation und Personal\nKalender mit Terminarten und Status\nAufgaben, To-dos und Team-Board mit Antwortfunktion\nUrlaubsplanung mit Urlaubsanträgen und Kontingenten\nPersonalakten\nDashboard mit den wichtigsten Kennzahlen\n\n6.2 Buchhaltung\nBuchhaltungsübersicht, Zahlungsabgleich und Auswertungen\n\n6.3 Test und Qualitätssicherung\nFunktionstests aller Module\nTestdurchlauf mit echten Beispieldaten des Auftraggebers\nFehlerbehebung\nAbschließender Gesamttest vor dem Livegang\n\n6.4 Schulung und Dokumentation\nBis zu [2] Schulungstermine für die Mitarbeitenden (online oder vor Ort nach Absprache)\nKurzanleitung für die wichtigsten Abläufe\n\n6.5 Livegang und Begleitung\nUmstellung auf den Echtbetrieb\nAbschließende Datenübernahme\n4 Wochen Begleitung nach dem Livegang mit kurzfristiger Fehlerbehebung" },
    { heading: "Nicht im Leistungsumfang enthalten", text: "Folgende Leistungen sind nicht enthalten und werden bei Bedarf gesondert angeboten:\nAnbindung externer Buchhaltungs- oder Banksoftware (z. B. DATEV, Online-Banking)\nOnline-Bezahlfunktionen (z. B. Kreditkarte, PayPal)\nNative Smartphone-Apps; die Web-Anwendung ist mobil nutzbar\nInhaltliche Erstellung von QM-Handbuch, Curricula oder Formularen; diese stellt der Auftraggeber bereit\nRechts- und Steuerberatung\nHosting-Kosten des Anbieters; sie werden nach tatsächlichem Aufwand weiterberechnet" },
    { heading: "Projektablauf", text: "Kick-off und Konzept: Workshop, Fachkonzept, Freigabe\nEntwicklung in Phasen: Jede Phase wird in der Test-Umgebung bereitgestellt\nRegelmäßige Abstimmung: alle 2 Wochen ein Termin von 60 Minuten mit Vorführung des aktuellen Stands\nAbnahme je Phase: Der Auftraggeber prüft innerhalb von 10 Werktagen und gibt die Phase frei oder meldet Mängel\nLivegang nach Abnahme aller Phasen" },
    { heading: "Zeitplan", text: "Projektstart: nach Annahme des Angebots zu vereinbaren · Projektdauer: 12 Monate\n\nMonate 1–2 — Phase 1: Fachkonzept freigegeben, Grundsystem und Datenübernahme\nMonate 3–4 — Phase 2: Teilnehmer, Kunden und Angebote nutzbar\nMonate 5–6 — Phase 3: Kurse, Klassenbuch und Anwesenheit nutzbar\nMonate 7–8 — Phase 4: Rechnungen, Mahnwesen und Vertrieb nutzbar\nMonate 9–10 — Phase 5: Prüfungen, Bildungsgutschein und AZAV-Modul nutzbar\nMonate 11–12 — Phase 6: Test, Schulung, Livegang" },
    { heading: "Vergütung", text: "Der Festpreis gilt für den unter „Leistungsumfang im Detail“ beschriebenen Umfang.\nÄnderungswünsche: Wünsche außerhalb des beschriebenen Umfangs werden vorab schriftlich mit Aufwand und Preis angeboten. Sie werden erst nach Freigabe umgesetzt (Stundensatz 75,00 € netto)." },
    { heading: "Zahlungsweise — monatliche Abschlagszahlungen", text: "12 monatliche Abschlagszahlungen zu je 11.250,00 € netto\nErste Abschlagsrechnung: Ende des ersten Projektmonats (Datum folgt nach feststehendem Projektstart)\nDanach jeweils zum Monatsende, letzte Abschlagsrechnung am Projektende\nZahlungsziel: 14 Tage nach Rechnungsstellung\nNach dem Livegang erfolgt eine Schlussrechnung mit Aufstellung aller geleisteten Abschläge" },
    { heading: "Nutzungsrechte und Quellcode", text: "Nach vollständiger Zahlung erhält der Auftraggeber die ausschließlichen, zeitlich und räumlich unbeschränkten Nutzungsrechte am entwickelten System\nDer Quellcode wird vollständig übergeben\nBis zur vollständigen Zahlung besteht ein einfaches Nutzungsrecht\nAllgemeines Fachwissen und allgemeine Programmbausteine darf der Auftragnehmer weiterhin verwenden" },
    { heading: "Gewährleistung", text: "12 Monate Gewährleistung ab Livegang\nFehler, die der Auftragnehmer zu vertreten hat, werden kostenfrei behoben\nKritische Fehler (System nicht nutzbar): Reaktion innerhalb von 1 Werktag\nSonstige Fehler: Reaktion innerhalb von 5 Werktagen" },
    { heading: "Wartung und Betrieb (optional, nach dem Livegang)", text: "Betrieb, Überwachung, Backups, Sicherheitsupdates, Support per E-Mail und Telefon, bis zu [5] Std. kleine Anpassungen pro Monat: [1.200,00] € netto/Monat\nLaufzeit 12 Monate, danach monatlich kündbar mit 3 Monaten Frist. Weitere Anpassungen werden zum Stundensatz von 75,00 € netto berechnet." },
    { heading: "Datenschutz", text: "Das System wird nach den Vorgaben der DSGVO umgesetzt: Zugriffsrechte, verschlüsselte Übertragung, Protokollierung\nVor Projektstart wird ein Vertrag zur Auftragsverarbeitung (AVV) abgeschlossen\nHosting auf Servern innerhalb der EU" },
    { heading: "Mitwirkung des Auftraggebers", text: "Der Auftraggeber:\nbenennt einen festen Ansprechpartner mit Entscheidungsbefugnis\nstellt Bestandsdaten, Vorlagen, Formulare, das QM-Handbuch und Curricula rechtzeitig bereit\nnimmt an den Abstimmungsterminen teil\nprüft und gibt die Phasen innerhalb von 10 Werktagen frei\nVerzögert sich die Mitwirkung, verschiebt sich der Zeitplan entsprechend." },
    { heading: "Umsatzsteuer", text: "Bei Leistung aus Zypern an ein Unternehmen in Deutschland: Die Rechnungen werden ohne Umsatzsteuer gestellt. Die Umsatzsteuer schuldet der Leistungsempfänger (Reverse-Charge-Verfahren, Art. 196 MwStSystRL, § 13b UStG)." },
  ],
};

const OFFER_TPL_PLATFORM = {
  title: "Entwicklung einer digitalen Bildungs- und KI-Plattform",
  subtitle: "Website, Buchungssystem, Verwaltungs-/Lernplattform, KI-Assistenz und Automatisierungen",
  intro: "Sehr geehrte Damen und Herren,\nvielen Dank für Ihr Interesse. Gerne unterbreiten wir Ihnen folgendes Angebot für die Konzeption und Entwicklung einer vollständigen digitalen Plattform nach dem bewährten Modell unserer eigenen Systemlandschaft:",
  items: [
    { title: "Konzeption, Design-System & öffentliche Website", scope: "ca. 200 Std.", description: "Editorial-Design, ca. 70 Seiten, 28 Kurs-/Produkt-Landingpages, interaktiver Karriere-Pfad, Einstufungsquiz, FAQ, B2B-Bereich, Rechtstexte, responsive", amount: 20000 },
    { title: "Buchungs- & Zahlungssystem", scope: "ca. 135 Std.", description: "Stripe (Karte, SEPA, Klarna), Ratenzahlung (3/6/12), automatische Bestätigungen, SEPA-Abgleich, Buchungs-PDFs", amount: 13500 },
    { title: "Verwaltungs- & Lernplattform „Campus“", scope: "ca. 225 Std.", description: "28 Admin-Module: Buchungen, Leads-Inbox, Benutzer-/Rollenverwaltung, CRM mit Mahnwesen, Statistik-Dashboards, Jobbörse, Outreach-Werkzeuge", amount: 22500 },
    { title: "KI-Assistenz (Produktlinie)", scope: "ca. 275 Std.", description: "25+ KI-Module auf Basis großer Sprachmodelle: Simulationen mit Scoring, Kriterienkataloge, Wissensdatenbank, Report-Generierung, Nutzungs-/Kostenkontrolle, Lernsystem", amount: 27500 },
    { title: "Dokumenten-Automatisierung (PDF-Engine)", scope: "ca. 90 Std.", description: "Flyer, Skripte, One-Pager, Verträge, Reports im Markendesign inkl. QR-Codes", amount: 9000 },
    { title: "E-Mail- & Marketing-Automation", scope: "ca. 70 Std.", description: "Transaktionsmails, Lead-Funnel mit automatischem Follow-up, Onboarding-Strecken", amount: 7000 },
    { title: "Marketing-Assets & Branding", scope: "ca. 70 Std.", description: "40+ Werbeanzeigen (KI-Bildwelten), Kampagnen-Landingpages, durchgängiges Corporate Design", amount: 7000 },
    { title: "Sicherheit, Auth & DSGVO", scope: "ca. 45 Std.", description: "Login-/Rechtesystem, Cookie-Consent, Datenschutz-Umsetzung, Backups", amount: 4500 },
    { title: "Qualitätssicherung & Inbetriebnahme", scope: "ca. 110 Std.", description: "dokumentierte Testzyklen, Deployment-Pipeline, Staging-/Produktionsumgebung, Iterationen", amount: 11000 },
  ],
  monthly: [],
  terms: [
    { heading: "Optional: Betrieb, Wartung & Weiterentwicklung nach Go-Live", text: "1.490 € netto/Monat (Hosting-Betreuung, Updates, Support, bis zu 10 Entwicklungsstunden/Monat inklusive)." },
    { heading: "Konditionen", text: "Projektlaufzeit: ca. 9–12 Monate, iterativ in 4 Ausbaustufen (Website & Buchung → Verwaltungsplattform → KI-Assistenz → Automation & Marketing); jede Stufe wird einzeln abgenommen und ist ab Abnahme produktiv nutzbar\nZahlungsplan: 30 % bei Beauftragung · je 30 % bei Abnahme der Ausbaustufen 2 und 3 · 10 % bei Gesamtabnahme\nTechnische Basis: React, FastAPI/Python, MongoDB, Stripe, Resend, Anbindung an große Sprachmodelle (OpenAI/Google/Anthropic); Quellcode geht nach vollständiger Zahlung in das Eigentum des Auftraggebers über\nLaufende Drittkosten (Hosting, KI-Nutzung, Zahlungs- und E-Mail-Dienste) trägt der Auftraggeber; wir richten alle Konten auf Ihren Namen ein\nGültigkeit: Dieses Angebot ist 30 Tage ab Angebotsdatum gültig\nAlle Preise verstehen sich netto — Umkehrung der Steuerpflicht (Reverse Charge) gemäß Art. 196 MwSt-SystRL i. V. m. § 13b UStG; die Umsatzsteuer ist vom Leistungsempfänger zu schulden" },
  ],
};

/* ---------------- Login ---------------- */

function Login({ onLogin }) {
  const [email, setEmail] = useState("admin@joudi-group.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await api("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      onLogin(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        onSubmit={submit}
        className="w-full max-w-sm rounded-[3px] border border-bone/10 bg-carddark p-8"
      >
        <LogoMark size={30} />
        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60">Admin-Bereich · Rechnungen</p>

        <label className="mt-6 block text-xs font-mono uppercase tracking-[0.2em] text-bone/60">E-Mail</label>
        <input
          data-testid="admin-login-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-2 w-full rounded-[3px] border border-bone/15 bg-dark px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
        />
        <label className="mt-4 block text-xs font-mono uppercase tracking-[0.2em] text-bone/60">Passwort</label>
        <input
          data-testid="admin-login-password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-[3px] border border-bone/15 bg-dark px-4 py-3 text-sm outline-none transition-colors focus:border-gold"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
            {error}
          </p>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={busy}
          className="mt-6 w-full rounded-[3px] bg-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone disabled:opacity-50"
        >
          {busy ? "ANMELDEN …" : "ANMELDEN"}
        </button>
      </motion.form>
    </div>
  );
}

/* ---------------- Invoice print sheet (A4, nach Vorlage) ---------------- */

function InvoiceSheet({ inv }) {
  return (
    <div id="invoice-sheet" className="mx-auto w-full max-w-[820px] bg-white text-[#161719] shadow-2xl">
      {/* Kopfband */}
      <div className="flex items-center justify-between bg-[#161719] px-7 py-4">
        <div className="flex items-center gap-3">
          <LogoMark size={34} />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/60">{ISSUER.web}</span>
        </div>
        <div className="text-right">
          <p className="font-display text-2xl font-bold tracking-wide text-white">INVOICE</p>
        </div>
      </div>

      <div className="px-7 py-4 text-[12px] leading-relaxed">
        {/* Aussteller */}
        <div className="flex justify-between gap-8">
          <div>
            <p className="font-display text-sm font-bold">{ISSUER.name}</p>
            {ISSUER.address.split("\n").map((l) => (
              <p key={l}>{l}</p>
            ))}
            <p className="mt-1">
              Tax ID No.:{" "}
              {inv.issuer_vat || <span className="text-[#6F6D66]">—</span>}
            </p>
          </div>
          <div className="text-right">
            <p>
              <span className="font-mono text-[11px] text-[#6F6D66]">Invoice No.:</span>{" "}
              <span className="font-semibold">{inv.number}</span>
            </p>
            <p>
              <span className="font-mono text-[11px] text-[#6F6D66]">Date:</span>{" "}
              <span className="font-semibold">{inv.date}</span>
            </p>
          </div>
        </div>

        {/* Empfänger */}
        <div className="mt-4 border-l-2 border-[#3D9EFF] bg-[#E6ECF3] px-4 py-3">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6F6D66]">Bill To:</p>
          <p className="mt-0.5 font-display text-base font-bold">{inv.client_name}</p>
          {inv.client_address.split("\n").map((l) => (
            <p key={l}>{l}</p>
          ))}
          <p className="mt-1">
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6F6D66]">VAT No.: </span>
            {inv.client_vat || <span className="text-[#6F6D66]">—</span>}
          </p>
        </div>

        {/* Positionen */}
        <table className="mt-4 w-full border-collapse text-[12px]">
          <thead>
            <tr className="bg-[#161719] text-white">
              <th className="border border-[#161719] px-3 py-1.5 text-left font-mono text-[10px] uppercase tracking-[0.2em]">No.</th>
              <th className="border border-[#161719] px-3 py-1.5 text-left font-mono text-[10px] uppercase tracking-[0.2em]">Description</th>
              <th className="border border-[#161719] px-3 py-1.5 text-right font-mono text-[10px] uppercase tracking-[0.2em]">Amount</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, i) => (
              <tr key={i}>
                <td className="border border-[#E3E1DC] px-3 py-2 align-top">{i + 1}</td>
                <td className="border border-[#E3E1DC] px-3 py-2">{it.description}</td>
                <td className="border border-[#E3E1DC] px-3 py-2 text-right">{eurEn(it.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Summen */}
        <div className="mt-3 flex justify-end">
          <table className="w-full max-w-[300px] border-collapse text-[12px]">
            <tbody>
              <tr>
                <td className="border border-[#E3E1DC] bg-[#E6ECF3] px-3 py-1.5 font-semibold">Subtotal</td>
                <td className="border border-[#E3E1DC] px-3 py-1.5 text-right">{eurEn(inv.subtotal)}</td>
              </tr>
              <tr>
                <td className="border border-[#E3E1DC] bg-[#E6ECF3] px-3 py-1.5 font-semibold">VAT 0 %</td>
                <td className="border border-[#E3E1DC] px-3 py-1.5 text-right">{eurEn(0)}</td>
              </tr>
              <tr>
                <td className="border border-[#161719] bg-[#161719] px-3 py-1.5 font-display font-bold text-white">Total</td>
                <td className="border border-[#161719] bg-[#161719] px-3 py-1.5 text-right font-display text-base font-bold text-white">
                  {eurEn(inv.total)}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-1.5 px-1 text-[10px] text-[#6b7684]">Reverse Charge</p>
        </div>

        <p className="mt-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#161719]">Reverse Charge.</p>

        {/* Zahlung */}
        <div className="mt-3 border border-[#E3E1DC] p-3 text-[11px]">
          <p className="font-display text-xs font-bold">PAY TO: {ISSUER.name}</p>
          <div className="mt-2 grid grid-cols-3 gap-3">
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#6F6D66]">Bank</p>
              <p className="min-h-[16px] font-semibold">{inv.bank_name || "—"}</p>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#6F6D66]">IBAN</p>
              <p className="min-h-[16px] font-semibold">{inv.bank_iban || "—"}</p>
            </div>
            <div>
              <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-[#6F6D66]">BIC / SWIFT</p>
              <p className="min-h-[16px] font-semibold">{inv.bank_swift || "—"}</p>
            </div>
          </div>
          <p className="mt-1.5 text-[9px] text-[#6F6D66]">
            Payment terms: payable within 14 days of the invoice date. Please quote the company name and invoice number {inv.number} with your payment.
          </p>
        </div>

        <p className="mt-3 text-center font-display text-xs font-bold tracking-wide">THANK YOU FOR YOUR BUSINESS!</p>
        <p className="mt-1.5 text-center text-[9px] text-[#6F6D66]">
          {ISSUER.name} · Tassou Markou, 8560 Peyia, Paphos, Cyprus · {ISSUER.web} · Tel. {ISSUER.phone} · Tax ID: {ISSUER.taxId}
        </p>
      </div>
    </div>
  );
}

/* ---------------- Formular ---------------- */

/* ---------------- Angebot print sheet (A4) ---------------- */

function offerKpis(off) {
  const blick = (off.terms || []).find((t) => t.heading === "Auf einen Blick")?.text || "";
  const k1 = { label: "Festpreis", value: eur(off.netto), sub: "Reverse Charge, 0 % USt." };
  const std = blick.match(/([\d.]+) Stunden \(Stundensatz ([\d,]+) €\)/);
  const moduleCount = ((off.intro || "").match(/(\d+) Module/) || [])[1];
  const k2 = std
    ? { label: "Umfang", value: moduleCount ? `${moduleCount} Module` : `${off.items.length} Positionen`, sub: `${std[1]} Std. à ${std[2]} €` }
    : { label: "Umfang", value: `${off.items.length} Positionen`, sub: "Festpreis je Position" };
  const zPhase = blick.match(/Zeitrahmen: Go-live nach Abnahme der Etappen ((?:E\d)(?: und E\d)?)/);
  const zDate = blick.match(/Zeitrahmen: Start ([\d.]+), Go-live ca\. ([\d.]+) \(rund ([\w ä]+)\)/);
  const k3 = zPhase
    ? { label: "Zeitraum", value: "nach Projektfortschritt", sub: `Go-live nach Abnahme ${zPhase[1]}` }
    : zDate
      ? { label: "Zeitraum", value: `rund ${zDate[3]}`, sub: `${zDate[1]} – ca. ${zDate[2]}` }
      : { label: "Gültigkeit", value: `${off.valid_days} Tage`, sub: `ab ${dEUR(off.date)}` };
  const pNew = blick.match(/Zahlung: ([\d.,]+ €) Anzahlung bei Beauftragung/);
  const pOld = blick.match(/Zahlung: ([\d.,]+ €) Anzahlung[^,]*, Rest in (\d+) Raten/);
  const k4 = pNew
    ? { label: "Zahlung", value: pNew[1], sub: "Anzahlung, weitere Rechnungen nach Leistung" }
    : pOld
      ? { label: "Zahlung", value: pOld[1], sub: `Anzahlung, danach ${pOld[2]} Raten` }
      : { label: "Zahlung", value: "laut Zahlungsplan", sub: "binnen 14 Tage" };
  return [k1, k2, k3, k4];
}

function OfferSheet({ off }) {
  const short = (off.client_name || "").replace(/\s*GmbH\s*$/i, "").trim() || "[Kundenname]";
  const until = addDays(off.date, off.valid_days);
  const hasScope = off.items.some((i) => i.scope);
  const title = (off.title || "").includes("[Kundenname]") ? off.title.replace("[Kundenname]", short) : off.title;
  return (
    <div id="offer-sheet" className="mx-auto w-full max-w-[820px] bg-white text-[#161719] shadow-2xl">
      <div className="flex items-center justify-between bg-[#161719] px-7 py-4">
        <div>
          <p className="font-display text-lg font-bold tracking-[0.14em] text-white">{OFFER_ISSUER.brand}</p>
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-bone/60">{OFFER_ISSUER.brandSub}</p>
        </div>
        <div className="text-right">
          <p className="font-display text-2xl font-bold tracking-wide text-white">ANGEBOT</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Angebot · vertraulich</p>
        </div>
      </div>

      <div className="px-7 py-4 text-[12px] leading-relaxed">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-[#3D9EFF]">
          Angebot Nr. {off.number} · Datum {dEUR(off.date)}
          {off.valid_days > 0 ? ` · Gültig ${off.valid_days} Tage${until ? ` (bis ${dEUR(until)})` : ""}` : ""}
        </p>
        <h1 className="mt-2 font-display text-2xl font-bold leading-tight tracking-tight">{title}</h1>
        {off.subtitle && <p className="mt-1 text-[13px] text-[#4A4A46]">{off.subtitle}</p>}
        {off.intro && <p className="mt-3 whitespace-pre-line">{off.intro}</p>}

        <div className="mt-4 grid grid-cols-2 gap-6 border-y border-[#E3E1DC] py-3">
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6F6D66]">Anbieter</p>
            <p className="mt-0.5 font-display text-sm font-bold">{OFFER_ISSUER.name}</p>
            {OFFER_ISSUER.address.split("\n").map((l) => (
              <p key={l}>{l}</p>
            ))}
          </div>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#6F6D66]">Kunde</p>
            <p className="mt-0.5 font-display text-sm font-bold">{off.client_name}</p>
            {off.client_contact && <p>{off.client_contact}</p>}
            {off.client_address.split("\n").map((l) => (
              <p key={l}>{l}</p>
            ))}
            {off.client_vat && <p className="mt-0.5">USt-IdNr.: {off.client_vat}</p>}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-4 gap-2">
          {offerKpis(off).map((k, i) => (
            <div key={i} className="bg-[#161719] px-3 py-2.5">
              <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-gold">{k.label}</p>
              <p className="mt-0.5 font-display text-sm font-bold text-white">{k.value}</p>
              <p className="mt-0.5 text-[9px] leading-snug text-bone/60">{k.sub}</p>
            </div>
          ))}
        </div>
        <img src="/angebot-hero.png" alt="Akademie-CRM Illustration" className="mt-4 w-full rounded-[4px]" />

        <h2 className="mt-5 font-display text-base font-bold text-[#3D9EFF]">{off.items_heading || "1. Leistungsumfang & Einzelpositionen"}</h2>
        <table className="mt-2 w-full border-collapse text-[11px]">
          <thead>
            <tr className="bg-[#161719] text-white">
              <th className="border border-[#161719] px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-[0.2em]">Nr.</th>
              <th className="border border-[#161719] px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-[0.2em]">Leistungsbereich</th>
              {hasScope && <th className="border border-[#161719] px-2.5 py-1.5 text-left font-mono text-[9px] uppercase tracking-[0.2em]">Umfang</th>}
              <th className="border border-[#161719] px-2.5 py-1.5 text-right font-mono text-[9px] uppercase tracking-[0.2em]">Preis (netto)</th>
            </tr>
          </thead>
          <tbody>
            {off.items.map((it, i) => (
              <tr key={i} className={i % 2 === 1 ? "bg-[#F7F8FA]" : ""}>
                <td className="border border-[#E3E1DC] px-2.5 py-2 align-top">{i + 1}</td>
                <td className="border border-[#E3E1DC] px-2.5 py-2 align-top">
                  <span className="font-display text-[12px] font-bold">{it.title}</span>
                  {it.description && <span className="block text-[10px] text-[#6F6D66]">{it.description}</span>}
                </td>
                {hasScope && <td className="border border-[#E3E1DC] px-2.5 py-2 align-top font-mono text-[10px]">{it.scope}</td>}
                <td className="border border-[#E3E1DC] px-2.5 py-2 text-right align-top">{eur(it.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-3 flex justify-end">
          <table className="w-full max-w-[320px] border-collapse text-[12px]">
            <tbody>
              <tr>
                <td className="border border-[#E3E1DC] bg-[#E6ECF3] px-3 py-1.5 font-semibold">Gesamtsumme Projekt (netto)</td>
                <td className="border border-[#E3E1DC] px-3 py-1.5 text-right">{eur(off.netto)}</td>
              </tr>
              <tr>
                <td className="border border-[#E3E1DC] bg-[#E6ECF3] px-3 py-1.5 font-semibold">USt. 0 %</td>
                <td className="border border-[#E3E1DC] px-3 py-1.5 text-right">{eur(0)}</td>
              </tr>
              <tr>
                <td className="border border-[#161719] bg-[#161719] px-3 py-1.5 font-display font-bold text-white">GESAMT / TOTAL</td>
                <td className="border border-[#161719] bg-[#161719] px-3 py-1.5 text-right font-display text-base font-bold text-white">{eur(off.netto)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-[9px] leading-relaxed text-[#6F6D66]">{REVERSE_CHARGE}</p>

        {off.monthly.length > 0 && (
          <>
            <h2 className="mt-6 font-display text-base font-bold text-[#3D9EFF]">2. Laufende Leistungen (optional, monatlich)</h2>
            <table className="mt-2 w-full border-collapse text-[11px]">
              <tbody>
                {off.monthly.map((m, i) => (
                  <tr key={i} className={i % 2 === 1 ? "bg-[#F7F8FA]" : ""}>
                    <td className="border border-[#E3E1DC] px-2.5 py-2 align-top">
                      <span className="font-display text-[12px] font-bold">{m.title}</span>
                      {m.description && <span className="block text-[10px] text-[#6F6D66]">{m.description}</span>}
                    </td>
                    <td className="w-28 border border-[#E3E1DC] px-2.5 py-2 text-right align-top">{eur(m.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {off.terms.map((t, i) => (
          <div key={i} className="mt-5">
            <h2 className="font-display text-base font-bold text-[#3D9EFF]">{t.heading}</h2>
            <p className="mt-1 whitespace-pre-line">{t.text}</p>
          </div>
        ))}

        <div className="mt-8 grid grid-cols-2 gap-10">
          <div className="border-t border-[#161719] pt-1 text-[10px] text-[#6F6D66]">Ort, Datum · Auftraggeber</div>
          <div className="border-t border-[#161719] pt-1 text-[10px] text-[#6F6D66]">Ort, Datum · Auftragnehmer</div>
        </div>

        <p className="mt-5 border-t border-[#E3E1DC] pt-2 text-center text-[9px] text-[#6F6D66]">{OFFER_ISSUER.footer}</p>
      </div>
    </div>
  );
}

function InvoiceForm({ onCreated, clientsVersion = 0 }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientVat, setClientVat] = useState("");
  const [issuerVat, setIssuerVat] = useState("");
  const [date, setDate] = useState(today());
  const [subject, setSubject] = useState("");
  const [leistungszeitraum, setLeistungszeitraum] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [bankName, setBankName] = useState("");
  const [bankIban, setBankIban] = useState("");
  const [bankSwift, setBankSwift] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const setItem = (i, field, value) => {
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [field]: value } : it)));
  };

  useEffect(() => {
    api("/admin/clients").then(setClients).catch(() => { /* Auswahl bleibt optional */ });
  }, [clientsVersion]);

  const applyClient = (id) => {
    setClientId(id);
    const c = clients.find((cl) => cl.id === id);
    if (c) {
      setClientName(c.name);
      setClientAddress(c.address);
      setClientVat(c.vat);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const created = await api("/admin/invoices", {
        method: "POST",
        body: JSON.stringify({
          client_name: clientName,
          client_address: clientAddress,
          client_vat: clientVat,
          issuer_vat: issuerVat,
          date,
          subject,
          leistungszeitraum,
          due_date: dueDate,
          note,
          items: items.map((it) => ({ description: it.description, amount: parseFloat(String(it.amount).replace(",", ".")) || 0 })),
          bank_name: bankName,
          bank_iban: bankIban,
          bank_swift: bankSwift,
        }),
      });
      onCreated(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-[3px] border border-bone/15 bg-dark px-3 py-2.5 text-sm outline-none transition-colors focus:border-gold";
  const labelCls = "block text-xs font-mono uppercase tracking-[0.15em] text-bone/60";

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      onSubmit={submit}
      className="overflow-hidden"
    >
      <div className="rounded-[3px] border border-bone/10 bg-carddark p-6" data-testid="invoice-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>Aus Kundenstamm übernehmen</label>
            <select data-testid="invoice-client-select" value={clientId} onChange={(e) => applyClient(e.target.value)} className={inputCls}>
              <option value="">— Frei eingeben —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Kunde *</label>
            <input data-testid="invoice-client-name" required value={clientName} onChange={(e) => setClientName(e.target.value)} className={inputCls} placeholder="Dickies Campus GmbH" />
          </div>
          <div>
            <label className={labelCls}>Kunden-USt-IdNr. (VAT No.)</label>
            <input data-testid="invoice-client-vat" value={clientVat} onChange={(e) => setClientVat(e.target.value)} className={inputCls} placeholder="DE…" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Kunden-Adresse</label>
            <textarea data-testid="invoice-client-address" rows={3} value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} className={inputCls} placeholder="Straße, PLZ Ort, Land" />
          </div>
          <div>
            <label className={labelCls}>Rechnungsdatum</label>
            <input data-testid="invoice-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Betreff (optional)</label>
            <input data-testid="invoice-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} placeholder="z. B. Lehrbuch Band 6 — Aufarbeitung & KDP-Produktion" />
          </div>
          <div>
            <label className={labelCls}>Leistungszeitraum (optional)</label>
            <input data-testid="invoice-leistungszeitraum" value={leistungszeitraum} onChange={(e) => setLeistungszeitraum(e.target.value)} className={inputCls} placeholder="z. B. Oktober 2026" />
          </div>
          <div>
            <label className={labelCls}>Fällig am (optional)</label>
            <input data-testid="invoice-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Verweis / Anmerkung (optional)</label>
            <input data-testid="invoice-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="z. B. Verweis: Angebot 2026-005 — je Modul eine eigene Rechnung" />
          </div>
          <div>
            <label className={labelCls}>Eigene USt-IdNr. (TIC, wenn vorhanden)</label>
            <input data-testid="invoice-issuer-vat" value={issuerVat} onChange={(e) => setIssuerVat(e.target.value)} className={inputCls} placeholder="CY…" />
          </div>
        </div>

        <div className="mt-6">
          <label className={labelCls}>Positionen</label>
          {items.map((it, i) => (
            <div key={i} className="mt-2 flex gap-3">
              <input
                data-testid={`invoice-item-desc-${i + 1}`}
                required={i === 0}
                value={it.description}
                onChange={(e) => setItem(i, "description", e.target.value)}
                className={`${inputCls} mt-0 flex-1`}
                placeholder="Beschreibung der Leistung"
              />
              <input
                data-testid={`invoice-item-amount-${i + 1}`}
                required={i === 0}
                value={it.amount}
                onChange={(e) => setItem(i, "amount", e.target.value)}
                className={`${inputCls} mt-0 w-36 text-right`}
                placeholder="0.00"
                inputMode="decimal"
              />
              {items.length > 1 && (
                <button
                  type="button"
                  data-testid={`invoice-item-remove-${i + 1}`}
                  onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
                  className="mt-1 rounded-[3px] border border-bone/15 p-2 text-bone/50 transition-colors hover:border-red-400 hover:text-red-300"
                  aria-label="Position entfernen"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            data-testid="invoice-item-add"
            onClick={() => setItems((prev) => [...prev, { description: "", amount: "" }])}
            className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-gold transition-colors hover:text-bone"
          >
            <Plus size={14} /> Position hinzufügen
          </button>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label className={labelCls}>Bank</label>
            <input data-testid="invoice-bank-name" value={bankName} onChange={(e) => setBankName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>IBAN</label>
            <input data-testid="invoice-bank-iban" value={bankIban} onChange={(e) => setBankIban(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>BIC / SWIFT</label>
            <input data-testid="invoice-bank-swift" value={bankSwift} onChange={(e) => setBankSwift(e.target.value)} className={inputCls} />
          </div>
        </div>

        {error && (
          <p data-testid="invoice-form-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
            {error}
          </p>
        )}
        <button
          data-testid="invoice-submit"
          type="submit"
          disabled={busy}
          className="mt-6 rounded-[3px] bg-gold px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone disabled:opacity-50"
        >
          {busy ? "ERSTELLE …" : "RECHNUNG ERSTELLEN"}
        </button>
      </div>
    </motion.form>
  );
}

/* ---------------- Bearbeiten (Stammdaten nachtragen) ---------------- */

function EditPanel({ invoice, onSaved, onCancel }) {
  const [clientVat, setClientVat] = useState(invoice.client_vat || "");
  const [issuerVat, setIssuerVat] = useState(invoice.issuer_vat || "");
  const [subject, setSubject] = useState(invoice.subject || "");
  const [leistungszeitraum, setLeistungszeitraum] = useState(invoice.leistungszeitraum || "");
  const [dueDate, setDueDate] = useState(invoice.due_date || "");
  const [note, setNote] = useState(invoice.note || "");
  const [bankName, setBankName] = useState(invoice.bank_name || "");
  const [bankIban, setBankIban] = useState(invoice.bank_iban || "");
  const [bankSwift, setBankSwift] = useState(invoice.bank_swift || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const inputCls = "mt-1 w-full rounded-[3px] border border-bone/15 bg-dark px-3 py-2.5 text-sm outline-none transition-colors focus:border-gold";
  const labelCls = "block text-xs font-mono uppercase tracking-[0.15em] text-bone/60";

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const updated = await api(`/admin/invoices/${invoice.number}`, {
        method: "PATCH",
        body: JSON.stringify({ client_vat: clientVat, issuer_vat: issuerVat, bank_name: bankName, bank_iban: bankIban, bank_swift: bankSwift, subject, leistungszeitraum, due_date: dueDate, note }),
      });
      onSaved(updated);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.35, ease: EASE }}
      onSubmit={save}
      className="overflow-hidden"
      data-testid="invoice-edit-panel"
    >
      <div className="rounded-[3px] border border-gold/40 bg-carddark p-6">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-gold">Rechnung {invoice.number} bearbeiten</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Kunden-USt-IdNr.</label>
            <input data-testid="edit-client-vat" value={clientVat} onChange={(e) => setClientVat(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Eigene USt-IdNr. (TIC)</label>
            <input data-testid="edit-issuer-vat" value={issuerVat} onChange={(e) => setIssuerVat(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Bank</label>
            <input data-testid="edit-bank-name" value={bankName} onChange={(e) => setBankName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>IBAN</label>
            <input data-testid="edit-bank-iban" value={bankIban} onChange={(e) => setBankIban(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>BIC / SWIFT</label>
            <input data-testid="edit-bank-swift" value={bankSwift} onChange={(e) => setBankSwift(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Betreff</label>
            <input data-testid="edit-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={inputCls} placeholder="z. B. Lehrbuch Band 6 — Aufarbeitung & KDP-Produktion" />
          </div>
          <div>
            <label className={labelCls}>Leistungszeitraum</label>
            <input data-testid="edit-leistungszeitraum" value={leistungszeitraum} onChange={(e) => setLeistungszeitraum(e.target.value)} className={inputCls} placeholder="z. B. Oktober 2026" />
          </div>
          <div>
            <label className={labelCls}>Fällig am</label>
            <input data-testid="edit-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Verweis / Anmerkung</label>
            <input data-testid="edit-note" value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="z. B. Verweis: Angebot 2026-005" />
          </div>
        </div>
        {error && (
          <p data-testid="invoice-edit-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
            {error}
          </p>
        )}
        <div className="mt-5 flex gap-3">
          <button
            data-testid="invoice-edit-save"
            type="submit"
            disabled={busy}
            className="rounded-[3px] bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone disabled:opacity-50"
          >
            {busy ? "SPEICHERE …" : "SPEICHERN"}
          </button>
          <button
            type="button"
            data-testid="invoice-edit-cancel"
            onClick={onCancel}
            className="rounded-[3px] border border-bone/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-bone/70 transition-colors hover:border-gold hover:text-gold"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </motion.form>
  );
}

/* ---------------- Angebote (Formular & Liste) ---------------- */

function OfferForm({ onCreated }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [intro, setIntro] = useState("");
  const [itemsHeading, setItemsHeading] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientContact, setClientContact] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientVat, setClientVat] = useState("");
  const [date, setDate] = useState(today());
  const [validDays, setValidDays] = useState(30);
  const [items, setItems] = useState([{ title: "", scope: "", description: "", amount: "" }]);
  const [monthly, setMonthly] = useState([]);
  const [terms, setTerms] = useState([{ heading: "", text: "" }]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api("/admin/clients").then(setClients).catch(() => { /* Auswahl bleibt optional */ });
  }, []);

  const loadTemplate = (tpl) => {
    setTitle(tpl.title);
    setSubtitle(tpl.subtitle);
    setIntro(tpl.intro);
    setItemsHeading(tpl.items_heading || "");
    setItems(tpl.items.map((i) => ({ ...i, amount: String(i.amount) })));
    setMonthly(tpl.monthly.map((i) => ({ ...i, amount: String(i.amount) })));
    setTerms(tpl.terms.map((t) => ({ ...t })));
  };

  const setRow = (setter, i, field, value) => {
    setter((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
  };
  const removeRow = (setter, i) => setter((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const num = (v) => parseFloat(String(v).replace(",", ".")) || 0;
      const created = await api("/admin/offers", {
        method: "POST",
        body: JSON.stringify({
          title, subtitle, intro, items_heading: itemsHeading,
          client_name: clientName,
          client_contact: clientContact,
          client_address: clientAddress,
          client_vat: clientVat,
          date, valid_days: Number(validDays) || 30,
          items: items.map((it) => ({ title: it.title, scope: it.scope, description: it.description, amount: num(it.amount) })),
          monthly: monthly.map((it) => ({ title: it.title, scope: it.scope, description: it.description, amount: num(it.amount) })),
          terms: terms.filter((t) => t.heading || t.text),
        }),
      });
      onCreated(created);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-[3px] border border-bone/15 bg-dark px-3 py-2.5 text-sm outline-none transition-colors focus:border-gold";
  const labelCls = "block text-xs font-mono uppercase tracking-[0.15em] text-bone/60";

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.4, ease: EASE }}
      onSubmit={submit}
      className="overflow-hidden"
    >
      <div className="rounded-[3px] border border-bone/10 bg-carddark p-6" data-testid="offer-form">
        <div className="flex flex-wrap gap-2">
          <button type="button" data-testid="tpl-platform" onClick={() => loadTemplate(OFFER_TPL_PLATFORM)} className="rounded-[3px] border border-gold/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-ink">
            Vorlage: Bildungs- & KI-Plattform (122.000 €)
          </button>
          <button type="button" data-testid="tpl-lms" onClick={() => loadTemplate(OFFER_TPL_LMS)} className="rounded-[3px] border border-gold/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-ink">
            Vorlage: Lernplattform LMS (90.000 €)
          </button>
          <button type="button" data-testid="tpl-crm" onClick={() => loadTemplate(OFFER_TPL_CRM)} className="rounded-[3px] border border-gold/50 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-ink">
            Vorlage: CRM- & Verwaltungssystem (135.000 €)
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Aus Kundenstamm übernehmen</label>
            <select
              data-testid="offer-client-select"
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                const c = clients.find((cl) => cl.id === e.target.value);
                if (c) {
                  setClientName(c.name);
                  setClientAddress(c.address);
                  setClientVat(c.vat || "");
                }
              }}
              className={inputCls}
            >
              <option value="">— Frei eingeben —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Kunde *</label>
            <input data-testid="offer-client-name" required value={clientName} onChange={(e) => setClientName(e.target.value)} className={inputCls} placeholder="[Kundenname] / Akademie GmbH" />
          </div>
          <div>
            <label className={labelCls}>Ansprechpartner</label>
            <input data-testid="offer-client-contact" value={clientContact} onChange={(e) => setClientContact(e.target.value)} className={inputCls} placeholder="[Ansprechpartner]" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Anschrift</label>
            <textarea data-testid="offer-client-address" rows={2} value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} className={inputCls} placeholder="[Anschrift]" />
          </div>
          <div>
            <label className={labelCls}>Kunden-USt-IdNr.</label>
            <input data-testid="offer-client-vat" value={clientVat} onChange={(e) => setClientVat(e.target.value)} className={inputCls} placeholder="DE…" />
          </div>
          <div>
            <label className={labelCls}>Angebotstitel *</label>
            <input data-testid="offer-title" required value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="Digitale Lernplattform …" />
          </div>
          <div>
            <label className={labelCls}>Untertitel</label>
            <input data-testid="offer-subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Angebotsdatum</label>
            <input data-testid="offer-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Gültigkeit (Tage)</label>
            <input data-testid="offer-valid-days" type="number" min={1} max={365} value={validDays} onChange={(e) => setValidDays(e.target.value)} className={inputCls} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Überschrift der Preistabelle</label>
            <input data-testid="offer-items-heading" value={itemsHeading} onChange={(e) => setItemsHeading(e.target.value)} className={inputCls} placeholder="1. Leistungsumfang & Einzelpositionen" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Einleitungstext (Brief)</label>
            <textarea data-testid="offer-intro" rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} className={inputCls} />
          </div>
        </div>

        <div className="mt-6">
          <label className={labelCls}>Einzelpositionen</label>
          {items.map((it, i) => (
            <div key={i} className="mt-3 rounded-[3px] border border-bone/10 p-3" data-testid={`offer-item-${i + 1}`}>
              <div className="flex flex-wrap gap-3">
                <input data-testid={`offer-item-title-${i + 1}`} required={i === 0} value={it.title} onChange={(e) => setRow(setItems, i, "title", e.target.value)} className={`${inputCls} mt-0 min-w-[180px] flex-1`} placeholder="Leistungsbereich" />
                <input data-testid={`offer-item-scope-${i + 1}`} value={it.scope} onChange={(e) => setRow(setItems, i, "scope", e.target.value)} className={`${inputCls} mt-0 w-28`} placeholder="ca. 200 Std." />
                <input data-testid={`offer-item-amount-${i + 1}`} required={i === 0} value={it.amount} onChange={(e) => setRow(setItems, i, "amount", e.target.value)} className={`${inputCls} mt-0 w-32 text-right`} placeholder="0.00" inputMode="decimal" />
                {items.length > 1 && (
                  <button type="button" data-testid={`offer-item-remove-${i + 1}`} onClick={() => removeRow(setItems, i)} className="mt-1 rounded-[3px] border border-bone/15 p-2 text-bone/50 transition-colors hover:border-red-400 hover:text-red-300" aria-label="Position entfernen">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              <input data-testid={`offer-item-desc-${i + 1}`} value={it.description} onChange={(e) => setRow(setItems, i, "description", e.target.value)} className={`${inputCls} mt-2`} placeholder="Umfang / Beschreibung der Position" />
            </div>
          ))}
          <button type="button" data-testid="offer-item-add" onClick={() => setItems((prev) => [...prev, { title: "", scope: "", description: "", amount: "" }])} className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-gold transition-colors hover:text-bone">
            <Plus size={14} /> Position hinzufügen
          </button>
        </div>

        <div className="mt-6">
          <label className={labelCls}>Laufende Leistungen (optional, monatlich)</label>
          {monthly.map((m, i) => (
            <div key={i} className="mt-2 flex gap-3">
              <input data-testid={`offer-monthly-title-${i + 1}`} value={m.title} onChange={(e) => setRow(setMonthly, i, "title", e.target.value)} className={`${inputCls} mt-0 flex-1`} placeholder="z. B. Hosting, Betrieb, Backups, SSL, Monitoring" />
              <input data-testid={`offer-monthly-amount-${i + 1}`} value={m.amount} onChange={(e) => setRow(setMonthly, i, "amount", e.target.value)} className={`${inputCls} mt-0 w-32 text-right`} placeholder="0.00" inputMode="decimal" />
              <button type="button" data-testid={`offer-monthly-remove-${i + 1}`} onClick={() => removeRow(setMonthly, i)} className="mt-1 rounded-[3px] border border-bone/15 p-2 text-bone/50 transition-colors hover:border-red-400 hover:text-red-300" aria-label="Laufende Position entfernen">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          <button type="button" data-testid="offer-monthly-add" onClick={() => setMonthly((prev) => [...prev, { title: "", scope: "", description: "", amount: "" }])} className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-gold transition-colors hover:text-bone">
            <Plus size={14} /> Monatliche Position hinzufügen
          </button>
        </div>

        <div className="mt-6">
          <label className={labelCls}>Konditionen / Ablauf / Hinweise (Blöcke)</label>
          {terms.map((t, i) => (
            <div key={i} className="mt-3 rounded-[3px] border border-bone/10 p-3" data-testid={`offer-term-${i + 1}`}>
              <div className="flex gap-3">
                <input data-testid={`offer-term-heading-${i + 1}`} value={t.heading} onChange={(e) => setRow(setTerms, i, "heading", e.target.value)} className={`${inputCls} mt-0 flex-1`} placeholder="z. B. 3. Projektablauf & Zeitplan" />
                {terms.length > 1 && (
                  <button type="button" data-testid={`offer-term-remove-${i + 1}`} onClick={() => removeRow(setTerms, i)} className="mt-1 rounded-[3px] border border-bone/15 p-2 text-bone/50 transition-colors hover:border-red-400 hover:text-red-300" aria-label="Block entfernen">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
              <textarea data-testid={`offer-term-text-${i + 1}`} rows={3} value={t.text} onChange={(e) => setRow(setTerms, i, "text", e.target.value)} className={`${inputCls} mt-2`} placeholder="Text des Blocks (Zeilenumbrüche möglich)" />
            </div>
          ))}
          <button type="button" data-testid="offer-term-add" onClick={() => setTerms((prev) => [...prev, { heading: "", text: "" }])} className="mt-3 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-gold transition-colors hover:text-bone">
            <Plus size={14} /> Block hinzufügen
          </button>
        </div>

        {error && (
          <p data-testid="offer-form-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
            {error}
          </p>
        )}
        <button data-testid="offer-submit" type="submit" disabled={busy} className="mt-6 rounded-[3px] bg-gold px-7 py-3 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone disabled:opacity-50">
          {busy ? "ERSTELLE …" : "ANGEBOT ERSTELLEN"}
        </button>
      </div>
    </motion.form>
  );
}

function OffersPanel({ onPrint }) {
  const [offers, setOffers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    api("/admin/offers").then(setOffers).catch((err) => setError(err.message));
  };

  useEffect(() => {
    load();
  }, []);

  const mark = async (number, status) => {
    try {
      setError("");
      const updated = await api(`/admin/offers/${number}`, { method: "PATCH", body: JSON.stringify({ status }) });
      setOffers((prev) => prev.map((o) => (o.number === number ? updated : o)));
    } catch (err) {
      setError(err.message);
    }
  };

  const uploadPdf = async (number, file) => {
    try {
      setError("");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/offers/${number}/attachment`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        let msg = "Upload fehlgeschlagen";
        try {
          const j = await res.json();
          if (typeof j.detail === "string") msg = j.detail;
        } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const updated = await res.json();
      setOffers((prev) => prev.map((o) => (o.number === number ? updated : o)));
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mt-0" data-testid="offers-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60">Angebote — A4-Druckblatt im Joudi-Design</p>
        <button data-testid="offer-add-toggle" onClick={() => setShowForm((v) => !v)} className="inline-flex items-center gap-2 rounded-[3px] bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone">
          <Plus size={14} /> {showForm ? "Formular schließen" : "Neues Angebot"}
        </button>
      </div>

      {showForm && (
        <div className="mt-4">
          <OfferForm onCreated={(off) => { setShowForm(false); load(); onPrint(off); }} />
        </div>
      )}

      {error && (
        <p data-testid="offers-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
          {error}
        </p>
      )}

      <div className="mt-6 overflow-x-auto rounded-[3px] border border-bone/10" data-testid="offers-table">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-carddark text-left font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">
              <th className="px-4 py-3">Nummer</th>
              <th className="px-4 py-3">Datum</th>
              <th className="px-4 py-3">Kunde</th>
              <th className="px-4 py-3 text-right">Netto</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Offizielles PDF</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((o, i) => (
              <tr key={o.id} className="border-t border-bone/10 hover:bg-goldsoft/20">
                <td data-testid={`offer-row-${i + 1}`} className="px-4 py-3 font-mono text-xs text-gold">{o.number}</td>
                <td className="px-4 py-3 text-bone/80">{o.date}</td>
                <td className="px-4 py-3">{o.client_name}</td>
                <td className="px-4 py-3 text-right">{eur(o.netto)}</td>
                <td className="px-4 py-3">
                  <span data-testid={`offer-status-${i + 1}`} className={`rounded-[3px] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${OFFER_STATUS_COLOR[o.status]}`}>
                    {OFFER_STATUS_LABEL[o.status]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {o.attachment ? (
                    <span className="inline-flex items-center gap-3">
                      <a
                        data-testid={`offer-pdf-link-${i + 1}`}
                        href={`${API}/api/admin/offers/${o.number}/attachment`}
                        title={o.attachment.filename}
                        className="inline-flex items-center gap-1.5 text-xs text-emerald-300 transition-colors hover:text-gold"
                      >
                        <FileDown size={14} /> PDF
                      </a>
                      <label data-testid={`offer-pdf-replace-${i + 1}`} className="inline-flex cursor-pointer items-center gap-1 text-[11px] text-bone/50 transition-colors hover:text-gold">
                        <Upload size={12} /> Ersetzen
                        <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`offer-pdf-input-${i + 1}`} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadPdf(o.number, f); e.target.value = ""; }} />
                      </label>
                    </span>
                  ) : (
                    <label data-testid={`offer-pdf-upload-${i + 1}`} className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-bone/60 transition-colors hover:text-gold">
                      <Upload size={14} /> Hinterlegen
                      <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`offer-pdf-input-${i + 1}`} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadPdf(o.number, f); e.target.value = ""; }} />
                    </label>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`offer-print-${i + 1}`} onClick={() => onPrint(o)} className="mr-3 inline-flex items-center gap-1.5 text-xs text-bone/70 transition-colors hover:text-gold">
                    <Printer size={14} /> Drucken
                  </button>
                  {o.status === "draft" && (
                    <button data-testid={`offer-sent-${i + 1}`} onClick={() => mark(o.number, "sent")} className="text-xs text-bone/70 transition-colors hover:text-gold">
                      Als gesendet
                    </button>
                  )}
                  {o.status === "sent" && (
                    <>
                      <button data-testid={`offer-accepted-${i + 1}`} onClick={() => mark(o.number, "accepted")} className="mr-3 text-xs text-bone/70 transition-colors hover:text-emerald-300">
                        Angenommen
                      </button>
                      <button data-testid={`offer-declined-${i + 1}`} onClick={() => mark(o.number, "declined")} className="text-xs text-bone/70 transition-colors hover:text-red-300">
                        Abgelehnt
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {offers.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-inkmuted">
                  Noch keine Angebote — starte mit einer Vorlage über „Neues Angebot".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Kundenstamm ---------------- */

function ClientRowForm({ initial, onSaved, onCancel, submitLabel }) {
  const [name, setName] = useState(initial?.name || "");
  const [address, setAddress] = useState(initial?.address || "");
  const [vat, setVat] = useState(initial?.vat || "");
  const [note, setNote] = useState(initial?.note || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const isEdit = Boolean(initial?.id);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = JSON.stringify({ name, address, vat, note });
      const saved = isEdit
        ? await api(`/admin/clients/${initial.id}`, { method: "PATCH", body })
        : await api("/admin/clients", { method: "POST", body });
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "mt-1 w-full rounded-[3px] border border-bone/15 bg-dark px-3 py-2.5 text-sm outline-none transition-colors focus:border-gold";
  const labelCls = "block text-xs font-mono uppercase tracking-[0.15em] text-bone/60";
  const t = (suffix) => (isEdit ? `client-edit-${suffix}` : `client-add-${suffix}`);

  return (
    <form onSubmit={submit} data-testid={isEdit ? "client-edit-form" : "client-add-form"} className="rounded-[3px] border border-gold/40 bg-carddark p-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className={labelCls}>Firma *</label>
          <input data-testid={t("name")} required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Dickies Campus GmbH" />
        </div>
        <div>
          <label className={labelCls}>USt-IdNr.</label>
          <input data-testid={t("vat")} value={vat} onChange={(e) => setVat(e.target.value)} className={inputCls} placeholder="DE…" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Adresse</label>
          <textarea data-testid={t("address")} rows={3} value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} placeholder="Straße, PLZ Ort, Land" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Notiz</label>
          <input data-testid={t("note")} value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} placeholder="z. B. Kleinunternehmer §19 — ab Notartermin aktiv" />
        </div>
      </div>
      {error && (
        <p data-testid="client-form-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
          {error}
        </p>
      )}
      <div className="mt-5 flex gap-3">
        <button
          type="submit"
          data-testid={isEdit ? "client-edit-save" : "client-add-submit"}
          disabled={busy}
          className="rounded-[3px] bg-gold px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone disabled:opacity-50"
        >
          {busy ? "SPEICHERE …" : submitLabel}
        </button>
        <button
          type="button"
          data-testid={isEdit ? "client-edit-cancel" : "client-add-cancel"}
          onClick={onCancel}
          className="rounded-[3px] border border-bone/15 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-bone/70 transition-colors hover:border-gold hover:text-gold"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

function ClientsPanel() {
  const [clients, setClients] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = () => {
    api("/admin/clients").then(setClients).catch((err) => setError(err.message));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (c) => {
    if (!window.confirm(`Kundeneintrag „${c.name}" wirklich löschen?`)) return;
    try {
      setError("");
      await api(`/admin/clients/${c.id}`, { method: "DELETE" });
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="mt-8" data-testid="clients-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bone/60">Kundenstamm — befüllt das Rechnungsformular</p>
        <button
          data-testid="client-add-toggle"
          onClick={() => {
            setShowAdd((v) => !v);
            setEditing(null);
          }}
          className="inline-flex items-center gap-2 rounded-[3px] bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
        >
          <Plus size={14} /> {showAdd ? "Abbrechen" : "Kunde anlegen"}
        </button>
      </div>

      {showAdd && (
        <div className="mt-4">
          <ClientRowForm initial={null} onSaved={() => { setShowAdd(false); load(); }} onCancel={() => setShowAdd(false)} submitLabel="KUNDE SPEICHERN" />
        </div>
      )}
      {editing && (
        <div className="mt-4">
          <ClientRowForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} submitLabel="SPEICHERN" />
        </div>
      )}

      {error && (
        <p data-testid="clients-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
          {error}
        </p>
      )}

      <div className="mt-4 divide-y divide-bone/10 rounded-[3px] border border-bone/10">
        {clients.map((c, i) => (
          <div key={c.id} data-testid={`client-row-${i + 1}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-4 transition-colors hover:bg-goldsoft/20">
            <div className="min-w-0">
              <p className="text-sm font-medium">{c.name}</p>
              {c.address && <p className="mt-0.5 whitespace-pre-line text-xs text-bone/60">{c.address}</p>}
              {(c.vat || c.note) && (
                <p className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] uppercase tracking-[0.15em] text-bone/40">
                  {c.vat && <span>VAT {c.vat}</span>}
                  {c.note && <span>{c.note}</span>}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-4">
              <button
                data-testid={`client-edit-${i + 1}`}
                onClick={() => {
                  setEditing(c);
                  setShowAdd(false);
                }}
                className="text-xs text-bone/70 transition-colors hover:text-gold"
              >
                Bearbeiten
              </button>
              <button
                data-testid={`client-delete-${i + 1}`}
                onClick={() => remove(c)}
                className="inline-flex items-center gap-1.5 text-xs text-bone/70 transition-colors hover:text-red-300"
              >
                <Trash2 size={13} /> Löschen
              </button>
            </div>
          </div>
        ))}
        {clients.length === 0 && (
          <p className="px-4 py-10 text-center text-sm text-inkmuted">Noch keine Kunden — lege den ersten über „Kunde anlegen" an.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */

function Dashboard({ user, onLogout }) {
  const [tab, setTab] = useState("invoices");
  const [clientsVersion, setClientsVersion] = useState(0);
  const [printOffer, setPrintOffer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [printInv, setPrintInv] = useState(null);
  const [actionError, setActionError] = useState("");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      setInvoices(await api("/admin/invoices"));
    } catch (err) {
      if (err.message.includes("angemeldet") || err.message.includes("Sitzung")) onLogout();
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mark = async (number, status) => {
    try {
      setActionError("");
      const updated = await api(`/admin/invoices/${number}`, { method: "PATCH", body: JSON.stringify({ status }) });
      setInvoices((prev) => prev.map((iv) => (iv.number === number ? updated : iv)));
    } catch (err) {
      setActionError(err.message);
    }
  };

  const uploadInvoicePdf = async (number, file) => {
    try {
      setActionError("");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/admin/invoices/${number}/attachment`, { method: "POST", credentials: "include", body: fd });
      if (!res.ok) {
        let msg = "Upload fehlgeschlagen";
        try {
          const j = await res.json();
          if (typeof j.detail === "string") msg = j.detail;
        } catch (_) { /* ignore */ }
        throw new Error(msg);
      }
      const updated = await res.json();
      setInvoices((prev) => prev.map((iv) => (iv.number === number ? updated : iv)));
    } catch (err) {
      setActionError(err.message);
    }
  };

  const doLogout = async () => {
    try {
      await api("/auth/logout", { method: "POST" });
    } catch (_) { /* Cookies trotzdem verwerfen */ }
    onLogout();
  };

  const onCreated = (inv) => {
    setShowForm(false);
    setPrintInv(inv);
    load();
  };

  const onEditSaved = (updated) => {
    setInvoices((prev) => prev.map((iv) => (iv.number === updated.number ? updated : iv)));
    setEditing(null);
    if (printInv && printInv.number === updated.number) setPrintInv(updated);
  };

  if (printInv) {
    return <PrintOverlay inv={printInv} onClose={() => setPrintInv(null)} />;
  }

  if (printOffer) {
    return <OfferPrintOverlay off={printOffer} onClose={() => setPrintOffer(null)} />;
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 gap-y-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Joudi Group · Admin</p>
          <h1 className="mt-1 font-display text-3xl font-medium tracking-tight">Rechnungen</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            data-testid="admin-new-invoice"
            onClick={() => {
              setTab("invoices");
              setShowForm((v) => !v);
            }}
            className="inline-flex items-center gap-2 rounded-[3px] bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
          >
            <Plus size={15} /> {showForm ? "Formular schließen" : "Neue Rechnung"}
          </button>
          <button
            data-testid="admin-logout"
            onClick={doLogout}
            className="inline-flex items-center gap-2 rounded-[3px] border border-bone/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-bone/70 transition-colors hover:border-gold hover:text-gold"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="admin-tabs">
        <button data-testid="tab-invoices" onClick={() => { setTab("invoices"); setClientsVersion((v) => v + 1); }} className={tab === "invoices" ? TAB_ACTIVE : TAB_IDLE}>
          Rechnungen
        </button>
        <button data-testid="tab-clients" onClick={() => setTab("clients")} className={tab === "clients" ? TAB_ACTIVE : TAB_IDLE}>
          Kunden
        </button>
        <button data-testid="tab-offers" onClick={() => setTab("offers")} className={tab === "offers" ? TAB_ACTIVE : TAB_IDLE}>
          Angebote
        </button>
        <button data-testid="tab-receipts" onClick={() => setTab("receipts")} className={tab === "receipts" ? TAB_ACTIVE : TAB_IDLE}>
          Quittungen
        </button>
      </div>

      {tab === "clients" ? (
        <ClientsPanel />
      ) : tab === "offers" ? (
        <OffersPanel onPrint={(off) => setPrintOffer(off)} />
      ) : (
        <>
          {showForm && <InvoiceForm onCreated={onCreated} clientsVersion={clientsVersion} />}
          {editing && <EditPanel invoice={editing} onSaved={onEditSaved} onCancel={() => setEditing(null)} />}

          {actionError && (
            <p data-testid="invoice-action-error" className="mt-4 rounded-[3px] border border-red-500/40 bg-red-500/10 px-4 py-2 text-xs text-red-300">
              {actionError}
            </p>
          )}

          <div className="mt-8 overflow-x-auto rounded-[3px] border border-bone/10" data-testid="invoice-table">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-carddark text-left font-mono text-[10px] uppercase tracking-[0.2em] text-bone/60">
              <th className="px-4 py-3">Nummer</th>
              <th className="px-4 py-3">Datum</th>
              <th className="px-4 py-3">Kunde</th>
              <th className="px-4 py-3 text-right">Betrag</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Anhang</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, i) => (
              <tr key={inv.id} className="border-t border-bone/10 hover:bg-goldsoft/20">
                <td data-testid={`invoice-row-${i + 1}`} className="px-4 py-3 font-mono text-xs text-gold">{inv.number}</td>
                <td className="px-4 py-3 text-bone/80">{inv.date}</td>
                <td className="px-4 py-3">{inv.client_name}</td>
                <td className="px-4 py-3 text-right">{eur(inv.total)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-[3px] px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${STATUS_COLOR[inv.status]}`}>
                    {STATUS_LABEL[inv.status]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {inv.attachment ? (
                    <span className="inline-flex items-center gap-3">
                      <a
                        data-testid={`invoice-pdf-link-${i + 1}`}
                        href={`${API}/api/admin/invoices/${inv.number}/attachment`}
                        title={inv.attachment.filename}
                        className="inline-flex items-center gap-1.5 text-xs text-emerald-300 transition-colors hover:text-gold"
                      >
                        <FileDown size={14} /> PDF
                      </a>
                      <label data-testid={`invoice-pdf-replace-${i + 1}`} className="inline-flex cursor-pointer items-center gap-1 text-[11px] text-bone/50 transition-colors hover:text-gold">
                        <Upload size={12} /> Ersetzen
                        <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`invoice-pdf-input-${i + 1}`} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadInvoicePdf(inv.number, f); e.target.value = ""; }} />
                      </label>
                    </span>
                  ) : (
                    <label data-testid={`invoice-pdf-upload-${i + 1}`} className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-bone/60 transition-colors hover:text-gold">
                      <Upload size={14} /> Hinterlegen
                      <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid={`invoice-pdf-input-${i + 1}`} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) uploadInvoicePdf(inv.number, f); e.target.value = ""; }} />
                    </label>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <a
                    data-testid={`invoice-pdf-download-${i + 1}`}
                    href={`${API}/api/admin/invoices/${inv.number}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Rechnung als PDF im Joudi-Layout erzeugen"
                    className="mr-2 inline-flex items-center gap-1.5 text-xs text-bone/70 transition-colors hover:text-gold"
                  >
                    <FileDown size={14} /> PDF
                  </a>
                  <button
                    data-testid={`invoice-print-${i + 1}`}
                    onClick={() => setPrintInv(inv)}
                    className="mr-2 inline-flex items-center gap-1.5 text-xs text-bone/70 transition-colors hover:text-gold"
                  >
                    <Printer size={14} /> Drucken
                  </button>
                  <button
                    data-testid={`invoice-edit-${i + 1}`}
                    onClick={() => setEditing(inv)}
                    className="mr-2 text-xs text-bone/70 transition-colors hover:text-gold"
                  >
                    Bearbeiten
                  </button>
                  {inv.status !== "paid" && (
                    <button
                      data-testid={`invoice-paid-${i + 1}`}
                      onClick={() => mark(inv.number, "paid")}
                      className="text-xs text-bone/70 transition-colors hover:text-emerald-300"
                    >
                      Als bezahlt
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-inkmuted">
                  Noch keine Rechnungen — lege die erste über „Neue Rechnung" an.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
}

/* ---------------- Druckansicht (nur das Blatt — kein Dashboard dahinter) ---------------- */

function PrintOverlay({ inv, onClose }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = `Rechnung ${inv.number} - Joudi Group Ltd`;
    return () => {
      document.title = previousTitle;
    };
  }, [inv]);
  return (
    <div className="print-root min-h-screen bg-dark px-4 py-6 print:min-h-0 print:p-0">
      <div className="mx-auto mb-4 flex max-w-[800px] justify-end gap-3 print:hidden">
        <button
          data-testid="invoice-print-confirm"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-[3px] bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
        >
          <Printer size={15} /> Drucken / PDF speichern
        </button>
        <button
          data-testid="invoice-print-close"
          onClick={onClose}
          className="inline-flex items-center gap-2 rounded-[3px] border border-bone/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-bone/70 transition-colors hover:border-gold hover:text-gold"
        >
          <X size={15} /> Schließen
        </button>
      </div>
      <InvoiceSheet inv={inv} />
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; height: auto !important; min-height: 0 !important; }
          #root, .print-root { min-height: 0 !important; }
          #invoice-sheet { width: 190mm !important; max-width: 190mm !important; box-shadow: none !important; }
          #invoice-sheet, #invoice-sheet * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ---------------- Angebots-Druckansicht ---------------- */

function OfferPrintOverlay({ off, onClose }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = `Angebot ${off.number} - ${OFFER_ISSUER.name}`;
    return () => {
      document.title = previousTitle;
    };
  }, [off]);
  return (
    <div className="print-root min-h-screen bg-dark px-4 py-6 print:min-h-0 print:p-0">
      <div className="mx-auto mb-4 flex max-w-[820px] justify-end gap-3 print:hidden">
        <button
          data-testid="offer-print-confirm"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 rounded-[3px] bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-bone"
        >
          <Printer size={15} /> Drucken / PDF speichern
        </button>
        <button
          data-testid="offer-print-close"
          onClick={onClose}
          className="inline-flex items-center gap-2 rounded-[3px] border border-bone/15 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-bone/70 transition-colors hover:border-gold hover:text-gold"
        >
          <X size={15} /> Schließen
        </button>
      </div>
      <OfferSheet off={off} />
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          html, body { margin: 0 !important; padding: 0 !important; background: #fff !important; height: auto !important; min-height: 0 !important; }
          #root, .print-root { min-height: 0 !important; }
          #offer-sheet { width: 190mm !important; max-width: 190mm !important; box-shadow: none !important; }
          #offer-sheet, #offer-sheet * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function AdminPage() {
  const [user, setUser] = useState(null); // null = prüfe Session, false = ausgeloggt
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    api("/auth/me")
      .then(setUser)
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  return (
    <div className="min-h-screen bg-dark font-sans text-bone print:min-h-0">
      {checking ? (
        <div className="flex min-h-screen items-center justify-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-bone/40">Lade …</p>
        </div>
      ) : user ? (
        <Dashboard user={user} onLogout={() => setUser(false)} />
      ) : (
        <Login onLogin={setUser} />
      )}
    </div>
  );
}
